import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, skip, Subject } from 'rxjs';
import { TriggeredTableDataSource } from './triggered.data-source';

export enum EndOfLoadingMode {
  EmptyData,
  LessDataThanPageSize,
  Explicit,
}

export interface LazyLoadingData<T> {
  data: T[];
  isFinished: boolean;
}

export function instanceOfLazyLoadingData<T>(object: any): object is LazyLoadingData<T> {
  return 'data' in object;
}

export class LazyLoadedTableDataSource<T> extends TriggeredTableDataSource<T> {
  readonly maxLoopCount = 20;
  private _pageIndex = 0;
  private isFinished = false;
  private pageIndexChanged = new Subject<number>();
  private maxLoopCountExceededSubject = new BehaviorSubject<boolean>(false);

  get pageIndex(): number {
    return this._pageIndex;
  }

  set pageIndex(value: number) {
    this._pageIndex = value;
    this.pageIndexChanged.next(value);
  }

  get refreshEnabledChanged$(): Observable<boolean> {
    return this.pageIndexChanged.pipe(
      filter(() => this.isRefreshEnabled),
      map(value => value <= 1),
      distinctUntilChanged(),
      skip(1),
    );
  }

  get isEmpty$(): Observable<boolean> {
    return this.isLoading$.pipe(
      map(() => this.data.length === 0 && this.isFinished),
      distinctUntilChanged(),
    );
  }

  get maxLoopCountExceeded$(): Observable<boolean> {
    return this.maxLoopCountExceededSubject.asObservable();
  }

  constructor(
    private readonly lazyDataLoader: (...args: any[]) => Promise<T[] | LazyLoadingData<T>>,
    private readonly pageSize: number,
    private readonly endOfLoadingMode = EndOfLoadingMode.LessDataThanPageSize,
  ) {
    super(null);
  }

  override clear(): void {
    this.pageIndex = 0;
    this.isFinished = false;
    this.maxLoopCountExceededSubject.next(false);
  }

  protected override canLoadData(): boolean {
    return super.canLoadData() && !this.isFinished;
  }

  protected canRefreshData(): boolean {
    return super.canRefreshData() && this.pageIndex <= 1;
  }

  protected override async loadDataInternal(): Promise<void> {
    const minDataLengthToLoad = 50;
    let totalDataLength = 0;
    let loopCount = 0;

    this.maxLoopCountExceededSubject.next(false);

    while (loopCount++ < this.maxLoopCount) {
      const result = await this.lazyDataLoader(this.pageSize, this.pageIndex);
      const data = (result as LazyLoadingData<T>)?.data ?? (result as T[]) ?? [];
      this.data = this.pageIndex === 0 ? data : this.data.concat(data);
      this.pageIndex = this.pageIndex + 1;
      totalDataLength += data.length;

      switch (this.endOfLoadingMode) {
        case EndOfLoadingMode.LessDataThanPageSize:
          this.isFinished = data.length < this.pageSize;
          return;
        case EndOfLoadingMode.EmptyData:
          this.isFinished = data.length === 0;
          if (this.isFinished || totalDataLength >= minDataLengthToLoad) {
            return;
          }
          break;
        case EndOfLoadingMode.Explicit:
          this.isFinished = (result as LazyLoadingData<T>)?.isFinished ?? true;
          if (this.isFinished || totalDataLength >= minDataLengthToLoad) {
            return;
          }
          break;
      }
    }

    this.maxLoopCountExceededSubject.next(true);
    this.isFinished = true;
  }

  protected override async refreshDataInternal(): Promise<void> {
    this.pageIndex = 0;
    this.isFinished = false;
    this.maxLoopCountExceededSubject.next(false);
    await super.refreshDataInternal();
  }
}
